/**
 * MobileWelcome Component
 * 
 * Elegant empty journal state for mobile with warm, personal first impression.
 * Uses left-aligned, TranscriptPanel-style design with expandable prompts.
 */

import React, { useState, useMemo } from 'react';
import { ListTodo, FileText, Youtube, Lightbulb, Brain, Sparkles, Eye, Plus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import StudibudiFace from '@/components/brand/StudibudiFace';
import './mobile.css';

// Full list of activation prompts (matching TranscriptPanel)
const MOBILE_PROMPTS: { icon: LucideIcon; title: string; example: string }[] = [
    { icon: ListTodo, title: 'Add to my todo list', example: 'Add "Learn backprop" to my todo list' },
    { icon: FileText, title: 'Create a note...', example: 'Create a note about the Krebs cycle' },
    { icon: Youtube, title: 'Find a YouTube video...', example: 'Find a YouTube video about linear regression' },
    { icon: Lightbulb, title: 'Help me understand...', example: 'Help me understand momentum' },
    { icon: Brain, title: 'Quiz me on a topic...', example: 'Quiz me on photosynthesis' },
    { icon: Sparkles, title: 'Build a quick explainer...', example: 'Create a 5-minute explainer on momentum' },
    { icon: Eye, title: 'Explain "This"...', example: 'Explain "this" section of my journal' },
];

const PRIMARY_PROMPTS = MOBILE_PROMPTS.slice(0, 4);

interface MobileWelcomeProps {
    journalName?: string;
    onTapToStart?: () => void;
    onAddBlock?: () => void;
    isOnboardingMode?: boolean;
    /** 'canvas' for main journal view, 'overlay' for talk sheet */
    variant?: 'canvas' | 'overlay';
    /** Called when a prompt is selected (passes the example text) */
    onPromptSelect?: (promptExample: string) => void;
}

// Time-based greeting helper
const getGreeting = (isOnboardingMode: boolean) => {
    if (isOnboardingMode) {
        return "Hey! I'm Studibudi.";
    }

    const hour = new Date().getHours();
    const greetings = {
        morning: ["Good morning! Ready to learn?", "Rise and shine! Let's study", "Morning brain boost time!"],
        afternoon: ["Let's tackle something new", "Afternoon learning mode", "Ready when you are!"],
        evening: ["Evening study session?", "Winding down with some learning", "Night owl study time"],
        night: ["Late night learning?", "Burning the midnight oil", "Still going strong!"],
    };

    let timeSlot: keyof typeof greetings;
    if (hour >= 5 && hour < 12) timeSlot = 'morning';
    else if (hour >= 12 && hour < 17) timeSlot = 'afternoon';
    else if (hour >= 17 && hour < 21) timeSlot = 'evening';
    else timeSlot = 'night';

    const options = greetings[timeSlot];
    return options[Math.floor(Math.random() * options.length)];
};

const MobileWelcome: React.FC<MobileWelcomeProps> = ({
    journalName,
    onTapToStart,
    onAddBlock,
    isOnboardingMode = false,
    variant = 'canvas',
    onPromptSelect,
}) => {
    const [showAll, setShowAll] = useState(false);

    // Time-based greeting
    const greeting = useMemo(() => getGreeting(isOnboardingMode), [isOnboardingMode]);

    // Prompt list based on showAll state
    const visiblePrompts = showAll ? MOBILE_PROMPTS : PRIMARY_PROMPTS;

    // Handle prompt click
    const handlePromptClick = (prompt: typeof MOBILE_PROMPTS[0]) => {
        if (onPromptSelect) {
            onPromptSelect(prompt.example);
        } else if (onTapToStart) {
            onTapToStart();
        }
    };

    // Unified design for both canvas and overlay variants
    return (
        <div className={`mobile-welcome ${variant}`}>
            <div className="mobile-welcome-content">
                {/* Avatar */}
                <div className="mobile-welcome-avatar">
                    <StudibudiFace className="h-14 w-11" />
                </div>

                {/* Greeting */}
                <p className="mobile-welcome-title">{greeting}</p>
                <p className="mobile-welcome-subtitle">
                    Here are a few things I can do, or ask me anything!
                </p>

                {/* Prompts - TranscriptPanel style rows */}
                <div className="mobile-welcome-prompts">
                    {visiblePrompts.map((prompt) => {
                        const Icon = prompt.icon;
                        return (
                            <button
                                key={prompt.title}
                                type="button"
                                onClick={() => handlePromptClick(prompt)}
                                className="mobile-welcome-prompt-row"
                            >
                                <div className="mobile-welcome-prompt-icon">
                                    <Icon className="h-4 w-4" />
                                </div>
                                <span className="mobile-welcome-prompt-text">{prompt.title}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Show more/fewer toggle */}
                <button
                    type="button"
                    onClick={() => setShowAll((prev) => !prev)}
                    className="mobile-welcome-toggle"
                >
                    {showAll ? 'Show fewer ideas' : 'Show more ideas'}
                </button>

                {/* Insert content footer */}
                {onAddBlock && (
                    <div className="mobile-welcome-insert-footer">
                        <span className="mobile-welcome-insert-label">Or add your own content</span>
                        <button
                            type="button"
                            onClick={onAddBlock}
                            className="mobile-welcome-insert-btn"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Insert</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileWelcome;
